import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PriceTicker.module.css';

function fmtPrice(n, decimals = 2) {
  if (!n && n !== 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtPct(n) {
  if (!n && n !== 0) return '—';
  // Yahoo returns changePercent as a small decimal (e.g. 0.0037) or whole number (0.37)
  // Normalise: if absolute value < 2, treat as decimal and multiply by 100
  const pct = Math.abs(n) < 2 ? n * 100 : n;
  return (pct >= 0 ? '+' : '') + pct.toFixed(2) + '%';
}

function fmtTime(isoString) {
  if (!isoString) return null;
  try {
    return new Date(isoString).toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch { return null; }
}

export default function PriceTicker({ financials }) {
  const {
    price, currency = 'USD',
    change, changePct, marketTime,
    preMarketPrice, preMarketChange, preMarketChangePct, preMarketTime,
    postMarketPrice, postMarketChange, postMarketChangePct, postMarketTime,
    marketState,
  } = financials;

  const changeColor  = change >= 0 ? styles.up : styles.down;
  const changeSign   = change >= 0 ? '+' : '';

  const { t } = useTranslation();
  const isPreMarket  = marketState === 'PRE';
  const isPostMarket = marketState === 'POST' || marketState === 'CLOSED';
  const isExtended   = isPreMarket || isPostMarket;

  const extPrice  = isPreMarket ? preMarketPrice      : postMarketPrice;
  const extChange = isPreMarket ? preMarketChange     : postMarketChange;
  const extPct    = isPreMarket ? preMarketChangePct  : postMarketChangePct;
  const extTime   = isPreMarket ? preMarketTime       : postMarketTime;
  const extLabel  = isPreMarket ? t('price_ticker.pre_market') : t('price_ticker.after_hours');
  const extColor  = extChange >= 0 ? styles.up : styles.down;

  return (
    <div className={styles.wrap}>
      {/* ── Regular market price ── */}
      <div className={styles.main}>
        <span className={styles.price}>{fmtPrice(price)}</span>
        <span className={styles.currency}>{currency}</span>
        <span className={`${styles.change} ${changeColor}`}>
          {changeSign}{fmtPrice(change)} &nbsp; {fmtPct(changePct)}
        </span>
      </div>
      {marketTime && (
        <div className={styles.timestamp}>
          {t('price_ticker.last_updated', { time: fmtTime(marketTime) })}
        </div>
      )}

      {/* ── Pre / post market ── */}
      {extPrice > 0 && (
        <div className={styles.ext}>
          <div className={styles.extMain}>
            <span className={styles.extPrice}>{fmtPrice(extPrice)}</span>
            <span className={styles.currency}>{currency}</span>
            <span className={`${styles.change} ${styles.extChange} ${extColor}`}>
              {extChange >= 0 ? '+' : ''}{fmtPrice(extChange)} &nbsp; {fmtPct(extPct)}
            </span>
          </div>
          <div className={styles.extLabel}>{extLabel}</div>
          {extTime && <div className={styles.timestamp}>{t('price_ticker.last_updated', { time: fmtTime(extTime) })}</div>}
        </div>
      )}
    </div>
  );
}
